import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timezone, TypeGuard } from '@principle-theorem/shared';
import { isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';
import { EXACT_NULL_SENTINEL } from '../../util/helpers';

export const AGREEMENT_HOLDERS_V2_RESOURCE_TYPE = 'agreementHolderV2';

export const AGREEMENT_HOLDERS_V2_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Agreement Holders V2 List',
      description: `
    Auto-detected accounts that act as ledgers for external payors (ACC, MOH, GDB, SDB).
    Not real patients — they hold charges and receipts on behalf of funded patients.
    `,
      idPrefix: AGREEMENT_HOLDERS_V2_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

/**
 * An account acting as a ledger for an external payor rather than
 * a real patient. `id` is the underlying debtor record id.
 * `label` is a human-readable name (scheme code or "Lastname, Firstname").
 */
export interface IExactV2AgreementHolder {
  id: string;
  label: string;
}

export interface IExactV2AgreementHolderFilters {
  /**
   * Holder account id (trans4.payorid or localpayor._record_id). Shares the
   * numeric id-space with patient debtor ids, which is what lets a caller
   * probe "is this patient id actually a holder account?" via filterRecords.
   */
  agreementHolderId: string;
}

function isExactV2AgreementHolder(
  data: unknown,
  logError: boolean
): data is IExactV2AgreementHolder {
  return TypeGuard.interface<IExactV2AgreementHolder>(
    {
      id: isString,
      label: isString,
    },
    logError
  )(data);
}

/**
 * Finds every account that acts as a holder rather than a real patient.
 *
 * The "payor" field on a ledger row (trans4.payorid) can mean three things:
 *   - sentinel (4294967295)        = no payor (private patient)
 *   - a row in the scheme-payor table (localpayor) = scheme payor (ACC, GDB-*, SDB-*, BD, etc.)
 *   - anything else                = a debtor account acting as a holder
 *
 * Both holder kinds must be excluded from the patient job loop: debtor-backed
 * holders would otherwise look like real patients, and scheme-payor ids can
 * collide with debtor ids at some practices. Union catches both.
 */
const AGREEMENT_HOLDERS_V2_QUERY = `
SELECT
  trans4.payorid::TEXT AS id,
  COALESCE(
    debtor.lastname || ', ' || debtor.firstname,
    trans4.payorid::TEXT
  ) AS label
FROM trans4
LEFT JOIN debtor4 debtor
  ON debtor._record_id = trans4.payorid
WHERE
  NOT COALESCE(trans4._is_deleted, false)
  AND trans4.payorid != ${EXACT_NULL_SENTINEL}
  AND trans4.payorid NOT IN (SELECT _record_id FROM localpayor)
GROUP BY trans4.payorid, debtor.lastname, debtor.firstname
UNION
SELECT
  _record_id::TEXT AS id,
  code AS label
FROM localpayor
ORDER BY
  label ASC
${OFFSET_PLACEHOLDER}
`;

export type ExactV2AgreementHolder = ISourceEntityTypes<
  IExactV2AgreementHolder,
  object,
  IExactV2AgreementHolderFilters
>;

export class AgreementHoldersV2SourceEntity extends BaseSourceEntity<ExactV2AgreementHolder> {
  sourceEntity = AGREEMENT_HOLDERS_V2_SOURCE_ENTITY;
  entityResourceType = AGREEMENT_HOLDERS_V2_RESOURCE_TYPE;
  sourceQuery = AGREEMENT_HOLDERS_V2_QUERY;
  verifySourceFn = isExactV2AgreementHolder;

  getSourceRecordId(data: IExactV2AgreementHolder): string {
    return data.id;
  }

  getSourceLabel(record: IExactV2AgreementHolder): string {
    return record.label;
  }

  translate(_data: IExactV2AgreementHolder, _timezone: Timezone): object {
    return {};
  }

  getFilterData(
    data: IExactV2AgreementHolder,
    _timezone: Timezone
  ): IExactV2AgreementHolderFilters {
    return {
      agreementHolderId: data.id,
    };
  }
}
